import React, { useState, useEffect } from "react";
import axios from "axios";
import { imgUrl } from "../admin/adminApi";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Plus, Check, Clock, ChevronDown, ChevronRight, Sparkles, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const discounted = (s) => {
  if (!s.on_offer || !s.offer_value) return null;
  const d = s.offer_type === "percent" ? s.price * (1 - s.offer_value / 100) : s.price - s.offer_value;
  return Math.max(0, Math.round(d));
};

export const ServiceRow = ({ s }) => {
  const { addToCart, items } = useCart();
  const [open, setOpen] = useState(false);
  const inCart = items.find((i) => i.name === s.name);
  const dp = discounted(s);
  const priceNow = dp != null ? dp : s.price;
  return (
    <div className="flex gap-3 bg-white rounded-xl ring-1 ring-black/5 shadow-sm p-3 hover:shadow-md transition-shadow">
      <img src={imgUrl(s.image)} alt={s.name} loading="lazy" className="w-20 h-20 rounded-lg object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1">{s.name}</h4>
          {dp != null && <span className="shrink-0 bg-accent/15 text-accent text-[10px] font-bold px-2 py-0.5 rounded-full">{s.offer_type === "percent" ? `${s.offer_value}% OFF` : `₹${s.offer_value} OFF`}</span>}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{s.description}</p>
        {s.description && <button onClick={() => setOpen(true)} data-testid={`view-details-${s.id}`} className="text-[11px] text-primary font-medium hover:underline inline-flex items-center gap-0.5 mt-0.5"><Info className="w-3 h-3" /> View Details</button>}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            {dp != null ? (<><span className="font-bold text-primary text-sm">₹{dp}</span><span className="line-through text-gray-400 text-xs">₹{s.price}</span></>)
              : <span className="font-bold text-primary text-sm">₹{s.price}</span>}
            {s.duration && <span className="text-[11px] text-gray-400 flex items-center gap-0.5"><Clock className="w-3 h-3" /> {s.duration}</span>}
          </div>
          <Button size="sm" onClick={() => addToCart({ name: s.name, price: priceNow, type: "service" })} disabled={!!inCart}
            className="h-7 px-3 rounded-full bg-primary hover:bg-primary/90 text-xs">
            {inCart ? <><Check className="w-3.5 h-3.5 mr-1" /> Added</> : <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>}
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden">
          <img src={imgUrl(s.image)} alt={s.name} className="w-full h-44 object-cover" />
          <div className="p-5">
            <DialogHeader>
              <DialogTitle className="font-display text-xl text-gray-900">{s.name}</DialogTitle>
            </DialogHeader>
            <div className="flex items-center gap-3 mt-2">
              {dp != null ? (<><span className="font-bold text-primary text-lg">₹{dp}</span><span className="line-through text-gray-400 text-sm">₹{s.price}</span></>)
                : <span className="font-bold text-primary text-lg">₹{s.price}</span>}
              {s.duration && <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration}</span>}
            </div>
            <p className="text-sm text-gray-600 mt-3 whitespace-pre-line leading-relaxed">{s.description}</p>
            <Button onClick={() => { addToCart({ name: s.name, price: priceNow, type: "service" }); setOpen(false); }} disabled={!!inCart} className="w-full mt-5 bg-primary hover:bg-primary/90 h-11">
              {inCart ? <><Check className="w-4 h-4 mr-1.5" /> Added to Cart</> : <><Plus className="w-4 h-4 mr-1.5" /> Add to Cart · ₹{priceNow}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ServicesSection = ({ initialCategory }) => {
  const { addToCart, items } = useCart();
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeCat, setActiveCat] = useState(initialCategory || null);
  const [activeSub, setActiveSub] = useState(null);

  useEffect(() => {
    if (initialCategory) { setActiveCat(initialCategory); setActiveSub(null); }
  }, [initialCategory]);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
    axios.get(`${API}/packages`).then((r) => setPackages(r.data)).catch(() => {});
    axios.get(`${API}/categories`).then((r) => {
      setCategories(r.data);
      if (!activeCat && r.data.length) setActiveCat(initialCategory || r.data[0].name);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const isPackages = activeCat === "Packages";

  const catServices = activeCat ? services.filter((s) => s.category === activeCat) : [];
  const subcats = [...new Set(catServices.map((s) => s.subcategory).filter(Boolean))];
  const rightServices = activeSub ? catServices.filter((s) => s.subcategory === activeSub) : catServices;

  const pickCat = (name) => { setActiveCat(name); setActiveSub(null); };

  return (
    <section id="categories" className="py-14 md:py-20 scroll-mt-24 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
        <div className="text-center mb-8">
          <p className="text-primary font-semibold tracking-wide uppercase text-sm">Home Salon Services</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mt-2">Explore Our Services</h2>
          <div className="w-20 h-1 bg-accent rounded-full mx-auto mt-4" />
        </div>

        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* LEFT: category list with inline sub-category drop */}
          <div className="bg-white rounded-2xl ring-1 ring-black/5 shadow-sm p-2 h-fit lg:sticky lg:top-24">
            {categories.map((c) => {
              const active = activeCat === c.name;
              const cSubs = [...new Set(services.filter((s) => s.category === c.name).map((s) => s.subcategory).filter(Boolean))];
              return (
                <div key={c.id}>
                  <button onClick={() => pickCat(c.name)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? "bg-primary/10" : "hover:bg-secondary"}`}>
                    <img src={imgUrl(c.image)} alt={c.name} className="w-9 h-9 rounded-full object-cover ring-1 ring-black/5" />
                    <span className={`flex-1 text-sm font-semibold uppercase tracking-wide ${active ? "text-primary" : "text-gray-700"}`}>{c.name}</span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${active ? "rotate-180 text-primary" : ""}`} />
                  </button>
                  {active && cSubs.length > 0 && (
                    <div className="pl-4 pr-2 py-1 space-y-0.5">
                      {cSubs.map((sub) => (
                        <button key={sub} onClick={() => setActiveSub(sub === activeSub ? null : sub)}
                          className={`w-full flex items-center gap-2 text-left text-sm px-3 py-2 rounded-lg transition-colors ${activeSub === sub ? "text-primary font-semibold bg-primary/5" : "text-gray-600 hover:text-primary"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${activeSub === sub ? "bg-primary" : "bg-gray-300"}`} /> {sub}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {packages.length > 0 && (
              <button onClick={() => { setActiveCat("Packages"); setActiveSub(null); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isPackages ? "bg-primary/10" : "hover:bg-secondary"}`}>
                <span className="w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center"><Sparkles className="w-4 h-4" /></span>
                <span className={`flex-1 text-sm font-semibold uppercase tracking-wide ${isPackages ? "text-primary" : "text-gray-700"}`}>Packages</span>
              </button>
            )}
          </div>

          {/* RIGHT: services of the selected category / sub-category */}
          <div>
            {isPackages ? (
              <>
                <div className="flex items-center gap-1.5 mb-4"><Sparkles className="w-5 h-5 text-accent" /><span className="font-display text-xl font-bold text-gray-900">Packages & Combos</span><span className="text-gray-400 ml-1">({packages.length})</span></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {packages.map((p) => {
                    const inCart = items.find((i) => i.name === p.name);
                    const pct = p.original_price ? Math.round((1 - p.price / p.original_price) * 100) : 0;
                    return (
                      <div key={p.id} className="flex gap-3 bg-white rounded-xl ring-1 ring-black/5 shadow-sm p-3">
                        <img src={imgUrl(p.image)} alt={p.name} className="w-20 h-20 rounded-lg object-cover shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2"><h4 className="font-semibold text-gray-900 text-sm flex-1">{p.name}</h4>{pct > 0 && <span className="shrink-0 bg-accent/15 text-accent text-[10px] font-bold px-2 py-0.5 rounded-full">{pct}% OFF</span>}</div>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{p.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2"><span className="font-bold text-primary text-sm">₹{p.price}</span>{p.original_price ? <span className="line-through text-gray-400 text-xs">₹{p.original_price}</span> : null}</div>
                            <Button size="sm" onClick={() => addToCart({ name: p.name, price: p.price, type: "package" })} disabled={!!inCart} className="h-7 px-3 rounded-full bg-primary hover:bg-primary/90 text-xs">{inCart ? <><Check className="w-3.5 h-3.5 mr-1" /> Added</> : <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>}</Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
            <>
            <div className="flex items-center gap-1.5 mb-4 text-sm">
              <span className="font-display text-xl font-bold text-gray-900">{activeCat || "Services"}</span>
              {activeSub && <><ChevronRight className="w-4 h-4 text-gray-400" /><span className="font-display text-xl font-bold text-primary">{activeSub}</span></>}
              <span className="text-gray-400 ml-1">({rightServices.length})</span>
            </div>
            {rightServices.length === 0 ? (
              <div className="bg-white rounded-2xl ring-1 ring-black/5 p-12 text-center text-gray-400">No services here yet.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {rightServices.map((s) => <ServiceRow key={s.id} s={s} />)}
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
